// Centralised asset loading. Vite hashes the imported URLs at build
// time; Pixi Assets.load() actually fetches and decodes the images so
// subsequent Texture.from(url) calls hit the cache instantly.

import { Assets, Texture } from 'pixi.js';

import cameraUrl from '../assets/camera.png';
import exitPadUrl from '../assets/exit_pad.png';
import floorUrl from '../assets/floor.png';
import intelUrl from '../assets/intel.png';
import lampUrl from '../assets/lamp_transparent.png';
import wallUrl from '../assets/wall.png';
import playerE from '../assets/player_directions/player_e.png';
import playerN from '../assets/player_directions/player_n.png';
import playerNe from '../assets/player_directions/player_ne.png';
import playerNw from '../assets/player_directions/player_nw.png';
import playerSa from '../assets/player_directions/player_s_a.png';
import playerSb from '../assets/player_directions/player_s_b.png';
import playerSe from '../assets/player_directions/player_se.png';
import playerSw from '../assets/player_directions/player_sw.png';
import playerW from '../assets/player_directions/player_w.png';
import guardE from '../assets/guard_directions/guard_e.png';
import guardIdle from '../assets/guard_directions/guard_idle.png';
import guardN from '../assets/guard_directions/guard_n.png';
import guardNe from '../assets/guard_directions/guard_ne.png';
import guardNw from '../assets/guard_directions/guard_nw.png';
import guardS from '../assets/guard_directions/guard_s.png';
import guardSe from '../assets/guard_directions/guard_se.png';
import guardSw from '../assets/guard_directions/guard_sw.png';
import guardW from '../assets/guard_directions/guard_w.png';
import guardKoUrl from '../assets/guard_ko_transparent.png';

const URLS = {
  floor: floorUrl,
  wall: wallUrl,
  camera: cameraUrl,
  intel: intelUrl,
  lamp: lampUrl,
  exitPad: exitPadUrl,
  // 8-direction player set. South (player facing the camera) has
  // two walk frames s_a / s_b alternated for animation; the other
  // seven cardinals/ordinals are static — the renderer's bob anim
  // adds the sense of motion when walking.
  'player.n': playerN,
  'player.ne': playerNe,
  'player.e': playerE,
  'player.se': playerSe,
  'player.s_a': playerSa,
  'player.s_b': playerSb,
  'player.sw': playerSw,
  'player.w': playerW,
  'player.nw': playerNw,
  // 8-direction guard set + idle. All directions are static; the
  // renderer's bob accumulator gives the sense of motion when moving.
  'guard.idle': guardIdle,
  'guard.n': guardN,
  'guard.ne': guardNe,
  'guard.e': guardE,
  'guard.se': guardSe,
  'guard.s': guardS,
  'guard.sw': guardSw,
  'guard.w': guardW,
  'guard.nw': guardNw,
  // Knocked-out body, lying on the ground. Used by the bodies layer
  // when guard.ko is true (and the body isn't hidden in a box).
  'guard.ko': guardKoUrl,
} as const;

export type AssetKey = keyof typeof URLS;

let loaded = false;

export async function loadAssets(): Promise<void> {
  if (loaded) return;
  await Assets.load(Object.values(URLS));
  // Source PNGs are high-resolution (some 1254×1254) but the in-game
  // sprites display them at as little as 22–40 px. Without mipmaps,
  // every output pixel samples ~one random source texel — that's what
  // made the lamp look like a pixelated disc. Turning on autoGenerate
  // mipmaps + linear filtering lets the GPU pick the right level of
  // detail per pixel and trilinear-blend, killing the aliasing.
  for (const url of Object.values(URLS)) {
    const t = Texture.from(url);
    t.source.scaleMode = 'linear';
    t.source.autoGenerateMipmaps = true;
    t.source.updateMipmaps();
  }
  loaded = true;
}

/** Get a cached Texture by asset key. Throws if loadAssets() hasn't
 * resolved yet — call sites should be downstream of the boot await. */
export function tex(key: AssetKey): Texture {
  return Texture.from(URLS[key]);
}
